import React, { useState } from 'react';
import { CreditCard, X, Plus, Edit3, Save, AlertCircle } from 'lucide-react';
import { usePermissions } from '../contexts/PermissionsContext';

const CreditsModal = ({ isOpen, onClose, userCredits, onUpdateCredits, targetUserId = null }) => {
  const { isAdmin } = usePermissions();
  const [editingLimit, setEditingLimit] = useState(false);
  const [newLimit, setNewLimit] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [loading, setLoading] = useState(false);
  
  
  if (!isOpen) return null;
  
  // Show loading state if userCredits is not available
  if (!userCredits) {
    return (
      <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]" 
      style={{
        zIndex: 9999,
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        visibility: 'visible',
        opacity: 1
      }} 
      data-testid="credits-modal"
    >
        <div className="bg-white rounded-lg shadow-xl p-6 max-w-lg w-full mx-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Credits Usage
            </h2>
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          <div className="text-center py-8">
            <div className="text-gray-500">Loading credits information...</div>
          </div>
        </div>
      </div>
    );
  }

  const usagePercentage = ((userCredits.monthlyCredits - userCredits.creditsRemaining) / userCredits.monthlyCredits) * 100;
  const isOutOfCredits = userCredits.creditsRemaining <= 0;
  const isLowCredits = userCredits.creditsRemaining <= userCredits.monthlyCredits * 0.1; // 10% or less remaining

  const handleTopUp = async (amount) => {
    if (!onUpdateCredits || loading) return;
    
    setLoading(true);
    try {
      await onUpdateCredits({
        action: 'topup',
        amount: amount,
        userId: targetUserId
      });
    } catch (error) {
      console.error('Failed to top up credits:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateLimit = async () => {
    if (!onUpdateCredits || !newLimit || loading) return;
    
    setLoading(true);
    try {
      await onUpdateCredits({
        action: 'updateLimit',
        newLimit: parseInt(newLimit),
        userId: targetUserId
      });
      setEditingLimit(false);
      setNewLimit('');
    } catch (error) {
      console.error('Failed to update credit limit:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCustomTopUp = async () => {
    const amount = parseInt(customAmount);
    if (!amount || amount <= 0) return;
    
    await handleTopUp(amount);
    setCustomAmount('');
    setShowCustomInput(false);
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]" 
      style={{
        zIndex: 9999,
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        visibility: 'visible',
        opacity: 1
      }} 
      data-testid="credits-modal"
    >
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-lg w-full mx-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            Credits Usage
          </h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        
        {/* Warning Banner */}
        {isOutOfCredits && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                  <X className="w-3 h-3 text-white" />
                </div>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Out of Credits
                </h3>
                <p className="text-sm text-red-700 mt-1">
                  You have used all your credits for this month. Please contact the administrator for more credits.
                </p>
              </div>
            </div>
          </div>
        )}
        
        {isLowCredits && !isOutOfCredits && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="w-5 h-5 bg-yellow-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-bold">!</span>
                </div>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  Low Credits Warning
                </h3>
                <p className="text-sm text-yellow-700 mt-1">
                  You're running low on credits. Consider contacting the administrator if you need more.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Credits Remaining</span>
            <span className={`font-semibold text-lg ${
              isOutOfCredits ? 'text-red-600' : 
              isLowCredits ? 'text-yellow-600' : 
              'text-green-600'
            }`}>
              {userCredits.creditsRemaining.toLocaleString()}
            </span>
          </div>
          
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Monthly Limit</span>
            <span className="font-semibold text-lg text-gray-900">
              {userCredits.monthlyCredits.toLocaleString()}
            </span>
          </div>
          
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Usage</span>
              <span className="text-sm text-gray-600">
                {usagePercentage.toFixed(1)}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className={`h-2 rounded-full transition-all duration-300 ${
                  isOutOfCredits ? 'bg-red-500' : 
                  isLowCredits ? 'bg-yellow-500' : 
                  'bg-blue-500'
                }`}
                style={{ width: `${Math.min(usagePercentage, 100)}%` }}
              />
            </div>
          </div>
          
          <div className="text-xs text-gray-500 mt-4">
            Credits reset monthly. {!isAdmin && 'Contact support if you need more credits.'}
          </div>
        </div>

        {/* Admin Controls */}
        {isAdmin && (
          <div className="border-t pt-4 mt-4">
            <h3 className="text-md font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-orange-500" />
              Admin Controls
            </h3>
            
            {/* Monthly Limit Management */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">Monthly Limit</label>
                {!editingLimit ? (
                  <button
                    onClick={() => {
                      setEditingLimit(true);
                      setNewLimit(userCredits.monthlyCredits.toString());
                    }}
                    className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                    disabled={loading}
                  >
                    <Edit3 className="w-3 h-3" />
                    Edit
                  </button>
                ) : (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleUpdateLimit}
                      className="flex items-center gap-1 text-sm text-green-600 hover:text-green-800"
                      disabled={loading || !newLimit}
                    >
                      <Save className="w-3 h-3" />
                      Save
                    </button>
                    <button
                      onClick={() => {
                        setEditingLimit(false);
                        setNewLimit('');
                      }}
                      className="text-sm text-gray-600 hover:text-gray-800"
                      disabled={loading}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
              
              {editingLimit ? (
                <input
                  type="number"
                  value={newLimit}
                  onChange={(e) => setNewLimit(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter new monthly limit"
                  disabled={loading}
                />
              ) : (
                <div className="px-3 py-2 bg-gray-50 rounded-lg text-sm">
                  {userCredits.monthlyCredits.toLocaleString()} credits
                </div>
              )}
            </div>

            {/* Credit Top-up */}
            <div className="mb-4">
              <label className="text-sm font-medium text-gray-700 mb-2 block">Top-up Credits</label>
              <div className="grid grid-cols-2 gap-2 mb-3">
                <button
                  onClick={() => handleTopUp(1000000)}
                  className="px-3 py-2 bg-green-100 text-green-800 rounded-lg hover:bg-green-200 transition-colors text-sm font-medium"
                  disabled={loading}
                >
                  +1M Credits
                </button>
                <button
                  onClick={() => handleTopUp(5000000)}
                  className="px-3 py-2 bg-blue-100 text-blue-800 rounded-lg hover:bg-blue-200 transition-colors text-sm font-medium"
                  disabled={loading}
                >
                  +5M Credits
                </button>
              </div>
              
              {/* Custom Amount */}
              {!showCustomInput ? (
                <button
                  onClick={() => setShowCustomInput(true)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm flex items-center justify-center gap-2"
                  disabled={loading}
                >
                  <Plus className="w-4 h-4" />
                  Custom Amount
                </button>
              ) : (
                <div className="flex gap-2">
                  <input
                    type="number"
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter custom amount"
                    disabled={loading}
                  />
                  <button
                    onClick={handleCustomTopUp}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    disabled={loading || !customAmount}
                  >
                    Add
                  </button>
                  <button
                    onClick={() => {
                      setShowCustomInput(false);
                      setCustomAmount('');
                    }}
                    className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    disabled={loading}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreditsModal;